import React, { memo, useMemo } from 'react';

import { Autocomplete } from '@material-ui/lab';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import type { AsyncState, FormulationDto, RouteDto } from '../../types/dtos/ema';
import { EMAProductForm, FLOW_TYPE_ADD, FLOW_TYPE_EDIT } from './EMAProductsReviewForm';
import Styles from './EMAProductsReview.styles';
import type {
  EmaProductFormData,
  EmaProductItem,
  EmaProductsDialog,
  EmaProductsReviewMode,
  EmaProductsReviewModel
} from './EMAProductsReview.types';
import { ViewData } from './EMAProductsReviewViewData';

/* eslint-disable no-unused-vars -- parameter names document the legacy form interface */
type ProductFormProps = {
  data: object;
  flowType: string;
  onSubmit: (data: EmaProductFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
  formulations: AsyncState<FormulationDto>;
  routes: AsyncState<RouteDto>;
  onRetryFormulations: () => Promise<void>;
  onRetryRoutes: () => Promise<void>;
  onAddFormulation: (value: string) => Promise<FormulationDto>;
  onAddRoute: (value: string) => Promise<RouteDto>;
  isSubmitDisabled: boolean;
};
/* eslint-enable no-unused-vars */

const ProductForm = EMAProductForm as unknown as React.ComponentType<ProductFormProps>;

type SearchOption = {
  id: string;
  label: string;
  tag: string;
};

type HeaderProps = {
  hasCurationIdentity: boolean;
  hasSelectedProduct: boolean;
  loading: boolean;
  mode: EmaProductsReviewMode;
  initialCount: number;
  sessionNewCount: number;
  onBack: () => void;
  onEdit: () => void;
  onAdd: () => void;
  onDone: () => void;
};

const HeaderView = ({
  hasCurationIdentity,
  hasSelectedProduct,
  loading,
  mode,
  initialCount,
  sessionNewCount,
  onBack,
  onEdit,
  onAdd,
  onDone
}: HeaderProps) => {
  const classes = Styles();
  const mutationsDisabled = !hasCurationIdentity || loading;
  const inForm = mode !== 'view';

  return (
    <Box className={classes.toolbar}>
      <Box className={classes.toolbarActions}>
        <Button className={classes.button} onClick={onBack} variant='contained'>
          Go back
        </Button>
        <Button
          className={classes.button}
          onClick={onEdit}
          disabled={mutationsDisabled || !hasSelectedProduct || inForm}
          variant='contained'>
          Edit
        </Button>
        <Button
          className={classes.button}
          onClick={onAdd}
          disabled={mutationsDisabled || inForm}
          variant='contained'>
          Add
        </Button>
        <Button
          className={classes.button}
          onClick={onDone}
          disabled={mutationsDisabled || inForm}
          variant='contained'>
          Done
        </Button>
      </Box>
      <Box>
        <Typography className={classes.count}>
          Number of Presentations (Products) in database: {initialCount}
        </Typography>
        <Typography className={classes.count}>
          Number of New Presentations (Products): {sessionNewCount}
        </Typography>
        <Typography className={classes.count}>
          Total Presentations (Products): {initialCount + sessionNewCount}
        </Typography>
      </Box>
    </Box>
  );
};

export const EMAProductsReviewHeader = memo(HeaderView);

type ProductSearchProps = {
  products: EmaProductItem[];
  /* eslint-disable-next-line no-unused-vars -- parameter documents the typed callback */
  onSelect: (productId: string | null) => void;
};

const ProductSearch = ({ products, onSelect }: ProductSearchProps) => {
  const classes = Styles();
  const options = useMemo<SearchOption[]>(
    () =>
      products.map(product => ({
        id: product.id,
        label: product.pno || 'Unknown',
        tag: product.indication_status || 'TBD'
      })),
    [products]
  );

  return (
    <Autocomplete<SearchOption>
      id='ema-product-search'
      fullWidth
      options={options}
      getOptionLabel={option => option.label}
      onChange={(_, option) => onSelect(option?.id ?? null)}
      renderOption={option => (
        <Box padding={1} display='flex' flex={1} justifyContent='space-between'>
          <Box flex={3}>
            <Typography>{option.label}</Typography>
          </Box>
          <Box flex={1}>
            <Typography>{option.tag}</Typography>
          </Box>
        </Box>
      )}
      noOptionsText='Not found! Please re-check the Application Number or Add a New Application.'
      renderInput={params => (
        <TextField
          /* eslint-disable-next-line react/jsx-props-no-spreading -- required by MUI Autocomplete */
          {...params}
          className={classes.search}
          fullWidth
          id='ema-product-applno'
          placeholder='Select an Application Number.'
          InputProps={{
            ...params.InputProps,
            classes: { input: classes.searchInput }
          }}
        />
      )}
    />
  );
};

type ReviewPanelProps = {
  model: EmaProductsReviewModel;
};

const ReviewPanelView = ({ model }: ReviewPanelProps) => {
  const classes = Styles();
  const { applicationNumber, currentUser, resources, selectedProduct, state, actions } = model;
  const inForm = state.mode !== 'view';
  const legacyFormData = useMemo(
    () => (selectedProduct ? { ...selectedProduct, id: undefined } : {}),
    [selectedProduct]
  );

  return (
    <Box className={classes.reviewBox}>
      <Box padding={1}>
        <Typography className={classes.heading}>
          Select a Presentation (Product) for EMEA-H-C-{applicationNumber}.
        </Typography>
      </Box>

      {!inForm ? (
        <Box padding={1}>
          <ProductSearch products={state.products} onSelect={actions.selectProduct} />
        </Box>
      ) : null}

      {!inForm && selectedProduct ? (
        /* eslint-disable-next-line react/jsx-props-no-spreading -- legacy view accepts product fields */
        <ViewData {...selectedProduct} />
      ) : null}

      {inForm ? (
        <ProductForm
          data={legacyFormData}
          flowType={state.mode === 'edit' ? FLOW_TYPE_EDIT : FLOW_TYPE_ADD}
          onSubmit={actions.requestSubmit}
          onCancel={actions.requestCancel}
          isLoading={state.loading}
          formulations={resources.formulations}
          routes={resources.routes}
          onRetryFormulations={actions.fetchFormulations}
          onRetryRoutes={actions.fetchRoutes}
          onAddFormulation={actions.addFormulation}
          onAddRoute={actions.addRoute}
          isSubmitDisabled={Boolean(
            currentUser &&
              currentUser['custom:role'] !== 'Admin' &&
              selectedProduct &&
              state.mode !== 'add' &&
              selectedProduct.indication_status === 'Qced'
          )}
        />
      ) : null}
    </Box>
  );
};

export const EMAProductsReviewPanel = memo(ReviewPanelView);

type PdfViewerProps = {
  pdfUrl: string;
};

const PdfViewerView = ({ pdfUrl }: PdfViewerProps) => {
  const classes = Styles();

  return pdfUrl ? (
    <Box display='flex' className={classes.pdfContainer}>
      <iframe title='pdf' src={pdfUrl} className={classes.pdfContainer} />
    </Box>
  ) : (
    <Box
      display='flex'
      justifyContent='center'
      alignItems='center'
      className={classes.pdfContainer}>
      <Typography className={classes.notAvailable}>No pdf is available</Typography>
    </Box>
  );
};

export const EMAProductsPdfViewer = memo(PdfViewerView);

const DIALOG_COPY: Record<EmaProductsDialog, { title: string; message: string }> = {
  submit: {
    title: 'Confirm Submission',
    message: 'Are you sure you want to submit the changes?'
  },
  cancel: {
    title: 'Confirm Cancel',
    message:
      'You have unsaved changes, are you sure that you want to cancel? All of your changes will be lost.'
  },
  done: {
    title: 'Complete Products Curation',
    message: 'Are you sure you have finished curating products for this batch?'
  }
};

type ConfirmationDialogProps = {
  dialog: EmaProductsDialog | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
};

const ConfirmationDialogView = ({ dialog, onClose, onConfirm }: ConfirmationDialogProps) => {
  const classes = Styles();
  const copy = dialog ? DIALOG_COPY[dialog] : null;

  return (
    <Dialog
      open={Boolean(copy)}
      onClose={onClose}
      aria-labelledby='ema-product-dialog-title'
      aria-describedby='ema-product-dialog-description'>
      <Box className={classes.dialog}>
        <DialogTitle id='ema-product-dialog-title'>{copy?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText id='ema-product-dialog-description'>{copy?.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} className={classes.confirm}>
            No
          </Button>
          <Button onClick={onConfirm} className={classes.confirm} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export const EMAProductsConfirmationDialog = memo(ConfirmationDialogView);
